import { createApi } from '@reduxjs/toolkit/query/react';

import { setPages, setSections } from 'store/wm/wmSlice';

import { baseQueryWithReauth } from 'utils/services';

import {
  AddReminderReqProps,
  CreateCheckInSurveyReqProps,
  CreateRenewalReqProps,
  CreateRenewalResProps,
  GetOrdersReqProps,
  GetOrdersResProps,
  GetPagesReqProps,
  GetPagesResProps,
  GetSurveyDetailsReqProps,
  GetSurveyDetailsResProps,
  GetSurveyReqProps,
  GetSurveyResProps,
  MarkPageViewedReqProps,
  MarkPageViewedResProps,
  NewDeviceMeasurementsReqProps,
  NewDeviceMeasurementsResProps,
  SelectRenewalOrderReqProps,
  SubmitSurveyReqProps,
  SubmitSurveyResProps,
  SurveyResponsesReqProps,
  SurveyResponsesResProps,
  UpdateSurveyReqProps,
  UpdateSurveyResProps
} from './wm.types';

export const wmApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    addReminder: build.mutation<unknown, AddReminderReqProps>({
      query: ({ surveyId, ...body }) => ({
        body,
        method: 'POST',
        url: `/wm/surveys/${surveyId}/reminder`
      })
    }),
    checkLabsTask: build.mutation<unknown, void>({
      query: () => ({
        url: '/tasks/create/lab-order-request'
      })
    }),
    createCheckInSurvey: build.mutation<unknown, CreateCheckInSurveyReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/wm/check-in-survey'
      })
    }),
    createRenewal: build.mutation<CreateRenewalResProps, CreateRenewalReqProps>({
      query: ({ orderId }) => ({
        method: 'POST',
        url: `/wm/orders/${orderId}/surveys/renewal`
      })
    }),
    getPages: build.query<GetPagesResProps, GetPagesReqProps>({
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded }) {
        const { data } = await cacheDataLoaded;
        if (data) {
          dispatch(
            setPages(data.data.map((page) => ({ ...page, section: page.section?.toLowerCase() })))
          );
          const sections = data.info?.sections.filter(
            (section) =>
              data.data.some((page) => page.section.toLowerCase() === section._id.toLowerCase()) ||
              !!data.info?.sections.some(({ parentIds }) => parentIds?.includes(section._id))
          );
          dispatch(setSections(sections));
        }
      },
      query: () => 'wm-pages'
    }),
    getSurvey: build.query<GetSurveyResProps, GetSurveyReqProps>({
      query: ({ type, orderId }) => ({
        url: `/wm/surveys/${type}/${orderId}`
      })
    }),
    getSurveyDetails: build.query<GetSurveyDetailsResProps, GetSurveyDetailsReqProps>({
      query: ({ type, orderId }) => ({ url: `/wm/survey-details/${type}/${orderId}` })
    }),
    getWMOrders: build.query<GetOrdersResProps, GetOrdersReqProps>({
      query: (params) => ({
        params,
        url: '/wm/orders'
      })
    }),
    markPageViewed: build.mutation<MarkPageViewedResProps, MarkPageViewedReqProps>({
      query: ({ wmPageId, ...body }) => ({
        body,
        method: 'PUT',
        url: `wm-pages/${wmPageId}`
      })
    }),
    newDeviceMeasurements: build.mutation<
      NewDeviceMeasurementsResProps,
      NewDeviceMeasurementsReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: `/wm/new-device-measurements`
      })
    }),
    selectRenewalOrder: build.mutation<unknown, SelectRenewalOrderReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: `/wm/select-renewal-order`
      })
    }),
    submitSurvey: build.mutation<SubmitSurveyResProps, SubmitSurveyReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: `/wm/submit-survey`
      })
    }),
    surveyResponses: build.mutation<SurveyResponsesResProps, SurveyResponsesReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: 'wm/survey-responses'
      })
    }),
    updateSurvey: build.mutation<UpdateSurveyResProps, UpdateSurveyReqProps>({
      query: ({ type, orderId, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/wm/surveys/${type}/${orderId}`
      })
    })
  }),
  reducerPath: 'wmApi',
  tagTypes: ['WeightManagement']
});

export const {
  useCreateCheckInSurveyMutation,
  useCreateRenewalMutation,
  useGetPagesQuery,
  useGetWMOrdersQuery,
  useGetSurveyDetailsQuery,
  useMarkPageViewedMutation,
  useSurveyResponsesMutation,
  useGetSurveyQuery,
  useUpdateSurveyMutation,
  useSubmitSurveyMutation,
  useNewDeviceMeasurementsMutation,
  useSelectRenewalOrderMutation,
  useAddReminderMutation,
  useCheckLabsTaskMutation
} = wmApi;
